/**
 * Color Scale Generator
 *
 * Generates 50-900 shade variations from any base color.
 * Works with the dynamic theme system - each theme gets its own scale.
 *
 * Usage:
 *   import { generateColorScale, injectColorVariables } from '@/utils/colorScale'
 *
 *   // Generate scale from a hex color
 *   const scale = generateColorScale('#4db6ac')
 *   // Returns: { 50: '#e0f2f1', 100: '#b2dfdb', ..., 900: '#004d40' }
 *
 *   // Inject as CSS variables (call on theme change)
 *   injectColorVariables(primaryColor, secondaryColor)
 */

interface RGB {
  r: number
  g: number
  b: number
}

interface HSL {
  h: number
  s: number
  l: number
}

interface ColorScale {
  [key: string]: string
}
/**
 * Convert hex to RGB
 * support #hex char(3, 6, 8)
 */
function hexToRgb(hex: string): RGB | null {
  if (!hex) return null

  let cleanHex = hex.replace('#', '')

  // Convert 3-char hex to 6-char (e.g., #RGB → #RRGGBB)
  if (cleanHex.length === 3) {
    cleanHex = cleanHex.split('').map(char => char + char).join('')
  }

  // Remove alpha channel if present (8-char to 6-char)
  if (cleanHex.length > 6) {
    cleanHex = cleanHex.slice(0, 6)
  }

  // Parse 6-char hex
  const result = /^([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(cleanHex)

  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : null
}

/**
 * Convert RGB to hex
 */
function rgbToHex(r: number, g: number, b: number): string {
  return (
    '#' +
    [r, g, b]
      .map(x => {
        const hex = Math.round(Math.max(0, Math.min(255, x))).toString(16)
        return hex.length === 1 ? '0' + hex : hex
      })
      .join('')
  )
}

/**
 * Convert RGB to HSL
 */
function rgbToHsl(r: number, g: number, b: number): HSL {
  r /= 255
  g /= 255
  b /= 255

  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  let h: number,
    s: number,
    l = (max + min) / 2

  if (max === min) {
    h = s = 0
  } else {
    const d = max - min
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6
        break
      case g:
        h = ((b - r) / d + 2) / 6
        break
      case b:
        h = ((r - g) / d + 4) / 6
        break
      default:
        h = 0
    }
  }

  return { h: h * 360, s: s * 100, l: l * 100 }
}

/**
 * Convert HSL to RGB
 */
function hslToRgb(h: number, s: number, l: number): RGB {
  h /= 360
  s /= 100
  l /= 100

  let r: number, g: number, b: number

  if (s === 0) {
    r = g = b = l
  } else {
    const hue2rgb = (p: number, q: number, t: number): number => {
      if (t < 0) t += 1
      if (t > 1) t -= 1
      if (t < 1 / 6) return p + (q - p) * 6 * t
      if (t < 1 / 2) return q
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6
      return p
    }

    const q = l < 0.5 ? l * (1 + s) : l + s - l * s
    const p = 2 * l - q
    r = hue2rgb(p, q, h + 1 / 3)
    g = hue2rgb(p, q, h)
    b = hue2rgb(p, q, h - 1 / 3)
  }

  return {
    r: Math.round(r * 255),
    g: Math.round(g * 255),
    b: Math.round(b * 255),
  }
}

/**
 * Generate a color scale from a base color
 *
 * @param {string} baseColor - Hex color (e.g., '#4db6ac')
 * @returns {object} Object with keys 50, 100, 200, 300, 400, 500, 600, 700, 800, 900
 */
export function generateColorScale(baseColor: string): ColorScale | null {
  const rgb = hexToRgb(baseColor)
  if (!rgb) {
    console.error('Invalid color:', baseColor)
    return null
  }

  const hsl = rgbToHsl(rgb.r, rgb.g, rgb.b)

  interface ShadeConfig {
    l: number
    s: number
  }

  const shades: Record<number, ShadeConfig> = {
    50: { l: 95, s: hsl.s * 0.3 },
    100: { l: 90, s: hsl.s * 0.4 },
    200: { l: 80, s: hsl.s * 0.5 },
    300: { l: 70, s: hsl.s * 0.7 },
    400: { l: 60, s: hsl.s * 0.85 },
    500: { l: hsl.l, s: hsl.s },
    600: { l: Math.max(hsl.l - 8, 25), s: Math.min(hsl.s * 1.05, 100) },
    700: { l: Math.max(hsl.l - 16, 20), s: Math.min(hsl.s * 1.1, 100) },
    800: { l: Math.max(hsl.l - 24, 15), s: Math.min(hsl.s * 1.15, 100) },
    900: { l: Math.max(hsl.l - 32, 10), s: Math.min(hsl.s * 1.2, 100) },
  }

  const scale: ColorScale = {}
  for (const [shade, config] of Object.entries(shades)) {
    const { r, g, b } = hslToRgb(hsl.h, config.s, config.l)
    scale[shade] = rgbToHex(r, g, b)
  }

  return scale
}

/**
 * Generate a neutral/grey scale
 */
export function generateNeutralScale(): ColorScale {
  return {
    50: '#fafafa',
    100: '#f5f5f5',
    200: '#eeeeee',
    300: '#e0e0e0',
    400: '#bdbdbd',
    500: '#9e9e9e',
    600: '#757575',
    700: '#616161',
    800: '#424242',
    900: '#212121',
  }
}

/**
 * Inject color CSS variables into the document
 *
 * @param {string} primaryColor - Primary hex color
 * @param {string} secondaryColor - Secondary hex color (optional)
 * @param {string} accentColor - Accent hex color (optional)
 */
export function injectColorVariables(
  primaryColor: string,
  secondaryColor?: string,
  accentColor?: string
): void {
  const root = document.documentElement

  const primaryScale = generateColorScale(primaryColor)
  const secondaryScale = secondaryColor
    ? generateColorScale(secondaryColor)
    : generateNeutralScale()
  const accentScale = accentColor ? generateColorScale(accentColor) : null
  const neutralScale = generateNeutralScale()

  if (primaryScale) {
    for (const [shade, color] of Object.entries(primaryScale)) {
      root.style.setProperty(`--color-primary-${shade}`, color)
    }
    root.style.setProperty('--color-primary', primaryColor)
  }

  if (secondaryScale) {
    for (const [shade, color] of Object.entries(secondaryScale)) {
      root.style.setProperty(`--color-secondary-${shade}`, color)
    }
    root.style.setProperty('--color-secondary', secondaryColor || '#9e9e9e')
  }

  if (accentScale) {
    for (const [shade, color] of Object.entries(accentScale)) {
      root.style.setProperty(`--color-accent-${shade}`, color)
    }
    root.style.setProperty('--color-accent', accentColor!)
  }

  for (const [shade, color] of Object.entries(neutralScale)) {
    root.style.setProperty(`--color-neutral-${shade}`, color)
  }

  root.style.setProperty('--color-success', '#4caf50')
  root.style.setProperty('--color-warning', '#ff9800')
  root.style.setProperty('--color-error', '#f44336')
  root.style.setProperty('--color-info', '#2196f3')

  root.style.setProperty('--color-surface', '#ffffff')
  root.style.setProperty('--color-background', '#fafafa')
  root.style.setProperty('--color-border', '#dedede')
}

/**
 * Get CSS variable value
 */
export function getColorVar(name: string): string {
  return getComputedStyle(document.documentElement)
    .getPropertyValue(name)
    .trim()
}

/**
 * Apply a color scale to CSS variables
 * Used by colorSystem.js plugin
 *
 * @param {string} name - Variable name prefix (e.g., 'primary')
 * @param {string} baseColor - Base hex color
 */
export function applyColorScale(name: string, baseColor: string): void {
  const root = document.documentElement
  const scale = generateColorScale(baseColor)

  if (scale) {
    for (const [shade, color] of Object.entries(scale)) {
      root.style.setProperty(`--color-${name}-${shade}`, color)
    }
    root.style.setProperty(`--color-${name}`, scale['500'])
  }
}

/**
 * Get contrast color (black or white) for text on a given background
 *
 * @param {string} hexColor - Background color in hex
 * @returns {string} '#ffffff' or '#000000'
 */
export function getContrastColor(hexColor: string): string {
  const rgb = hexToRgb(hexColor)
  if (!rgb) return '#000000'

  const luminance = (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255

  return luminance > 0.5 ? '#000000' : '#ffffff'
}

export default {
  generateColorScale,
  generateNeutralScale,
  injectColorVariables,
  applyColorScale,
  getContrastColor,
  getColorVar,
  hexToRgb,
  rgbToHex,
}
